import React, { useContext, useState, useEffect } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Col, 
  Row
} from 'reactstrap';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { DetailLayoutView } from 'components/DetailView';
import _ from 'lodash';
import styled from 'styled-components';
import { ToastContainer } from 'react-toastify';

import { errorNotify, successNotify } from 'utils/toaster';
import { Config } from '../../config';
import NewDetailView from 'components/DetailView/NewDetailScreen';
import { useHistory, useParams } from 'react-router-dom';
import { UserContext } from 'context/UserContext';
import { GetBroadcastQuery, UpdateBroadcastMutation, DeleteBroadcastMetadataMutation } from 'graphql/chat';
import CustomSpinner from 'components/CustomSpinner';

const PreviewBroadcast = () => {
  const { id } = useParams();

  const { token, user, isAdmin } = useContext(UserContext);
  const [updateBroadcast, { loading: updatingBroadcast, error }] = useMutation(UpdateBroadcastMutation); 
  const [deleteBroadcastMetadata] = useMutation(DeleteBroadcastMetadataMutation)

  const [preview, setPreview] = useState('');
  const [imageUrlFile, setImageUrlFile] = useState();
  const [draftModal, setDraftModal] = useState(false);
  const [publishModal, setPublishModal] = useState(false);

  const { data: broadcastData, loading: broadcastLoading, refetch } = useQuery(GetBroadcastQuery, {
    fetchPolicy: 'network-only',
    variables: {
      id,
      userId: user?.id
    }
  });

  const [values, setValues] = useState({
    broadcastTitle: '',
    broadcastMessage: '',
    broadcastButtonLink: '',
    broadcastImage: ''
  });

  useEffect(() => {
    setValues({
      broadcastTitle: data?.broadcastTitle || '',
      broadcastMessage: data?.broadcastMessage || '',
      broadcastButtonLink: data?.broadcastButtonLink || '',
      broadcastImage: data?.broadcastImage || '',
    });
  }, [broadcastData]);

  const data = broadcastData?.chat[0];
  
  const handleImageUpload = async ({ target: { files } }) => {
    const imageSizeLimit = 500;
    const reader = new FileReader();

    if (files) {
      reader.readAsDataURL(files[0]);
    }

    const imageSize = files[0]?.size / 1024;

    if (imageSize > imageSizeLimit) {
      return errorNotify(
        'The size of the image should be less than equal to 500kb'
      );
    }

    setImageUrlFile(files[0]);

    reader.onloadend = function (e) {
      setPreview(reader.result);
    };
  };

  const handleUpdateBroadcast = async (arg) => {
  const {
    broadcastTitle,
    broadcastMessage,
    broadcastButtonLink
  } = values;

  const publish = !!arg ? false : true;

  try {
      if (broadcastTitle && broadcastMessage && isAdmin) {
        let latestImage;

        if (!!imageUrlFile) {
          const form = new FormData();
          form.append('imageUrl', imageUrlFile);

          const res = await fetch(`${Config.restApiUrl}/uploads/content/upload-image`, {
            method: 'POST',
            headers: {
              "Authorization": token,
            },
            body: form,
          });

          const response = await res.json();
          latestImage = response?.data?.imageUrl;
        }

        const set = {
          broadcastTitle: broadcastTitle.trim(),
          broadcastMessage: broadcastMessage.trim(),
          broadcastButtonLink: broadcastButtonLink.trim(),
          isBroadcastLive: publish,
        };

        if (!!latestImage) {
          set.broadcastImage = latestImage;
        }

        if (publish) {
          // clear broadcast metadata
          await deleteBroadcastMetadata({
            variables: {
              broadcasterId: user?.id
            }
          })
        }

        const res = await updateBroadcast({
          variables: {
            id,
            set
          }
        });

        await refetch();

        if (publish) {
          successNotify('Broadcast updated successfully');
        } else {
          successNotify('Saved to draft successfully');
        }
      } else {
        errorNotify('Some fields are required');
      }
    } catch (error) {
      errorNotify('Something went wrong');
    }
  };

  return (
    <Wrapper>
      <Row>
        <Col>
          <DetailLayoutView
            image={!!preview ? preview : values.broadcastImage}
            onChange={handleImageUpload}
            uploadName='Broadcast (600px by 600px)'
            breadcrumbTitle='Broadcasts'
            breadcrumbItem={values.broadcastTitle}
            breadcrumbRoute={"/broadcasts"}
          >
            <NewDetailView
              key1={{
                label: 'Broadcast Title',
                placeholder: 'Broadcast Title',
                name: 'broadcastTitle',
                status: 'create',
                type: 'input',
              }}
              key2={{
                label: 'Broadcast Message',
                placeholder: 'Broadcast Message',
                name: 'broadcastMessage',
                status: 'create',
                type: 'description',
              }}
              key3={{
                label: 'Broadcast Button Link',
                placeholder: 'Optional',
                name: 'broadcastButtonLink',
                status: 'create',
                type: 'input',
              }}
              action={{
                type: 'action',
                disabled: _.isEmpty(values.broadcastTitle) || _.isEmpty(values.broadcastMessage),
                actionText: {
                  btn2Text: 'Save to Draft',
                  btn3Text: 'Update and Go Live',
                },
                action: {
                  btn2: () => setDraftModal(true),
                  btn3: () => setPublishModal(true),
                },
                actionLoading: {
                  btn3Loading: updatingBroadcast,
                },
              }}
              values={values}
              setValues={setValues}
              setDraftModal={() => setDraftModal(true)}
            />
          </DetailLayoutView>
        </Col>
      </Row>

      {/* draft publishModal */}
			<Modal isOpen={draftModal} centered={true}>
        <ModalBody>
          <p>
            Are you sure you want to save broadcast to Draft
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={() => setDraftModal(false)}>
            No
          </Button>{' '}
          <Button
            color='primary'
            onClick={() => {
              handleUpdateBroadcast("draft");
              setDraftModal(false)
            }}
          >
            Yes
          </Button>
        </ModalFooter>
			</Modal>

      {/* publish publishModal */}
      <Modal isOpen={publishModal} centered={true}>
        <ModalBody>
          <p>Are you sure you want to update broadcast to all users?</p>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={() => setPublishModal(false)}>
            No
          </Button>{' '}
          <Button
            color='primary'
            onClick={() => {
              handleUpdateBroadcast();
              setPublishModal(false);
            }}
          >
            {updatingBroadcast ? (
              <CustomSpinner />
            ) : (
              'Yes'
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer />
    </Wrapper>
  );
};

export const Wrapper = styled.div`
  margin-top: 10vh;
`;

export default PreviewBroadcast;
